import { useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Package, Users, FileText, FilePlus,
  ShoppingCart, ClipboardList, Settings, LogOut, Menu, X,
  ChevronDown, ChevronRight, UserCog, FileSpreadsheet, Sprout,
  Wallet, BarChart3, Heart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/products", label: "Products", icon: Package },
  { path: "/customers", label: "Customers", icon: Users },
  {
    label: "Invoices", icon: FileText,
    children: [
      { path: "/invoices/create", label: "New Invoice", icon: FilePlus },
      { path: "/invoices", label: "All Invoices", icon: FileText },
    ],
  },
  { path: "/purchases", label: "Purchases", icon: ShoppingCart },
  { path: "/expenses", label: "Expenses", icon: Wallet },
  { path: "/stock-registers", label: "Stock Registers", icon: ClipboardList },
  { path: "/sales-reports", label: "Sales & Profits", icon: BarChart3 },
  { path: "/gst-reports", label: "GST Reports", icon: FileSpreadsheet },
  { path: "/settings", label: "Settings", icon: Settings, adminOnly: true },
  { path: "/users", label: "Users", icon: UserCog, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ Invoices: true });

  const isAdmin = ["superadmin", "shop_admin"].includes(user?.role);
  const filteredNav = navItems.filter((item) => !item.adminOnly || isAdmin);

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ item, nested = false }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200
          ${nested ? "pl-10" : ""}
          ${active
            ? "bg-[#2D5438] text-white shadow-sm"
            : "text-[#1A2E20] hover:bg-[#E8F0E5]"
          }`}
      >
        {Icon && <Icon size={18} strokeWidth={1.5} />}
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-[#DCE6DD]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2D5438] flex items-center justify-center">
            <Sprout size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-[#2D5438] font-['Manrope']">SuperAgri</h1>
            <p className="text-[10px] text-[#6B8573] tracking-wider uppercase">superagri.in</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNav.map((item) =>
          item.children ? (
            <div key={item.label}>
              <button
                data-testid={`nav-group-${item.label.toLowerCase()}`}
                onClick={() => toggleGroup(item.label)}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm text-[#1A2E20] hover:bg-[#E8F0E5] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <item.icon size={18} strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </div>
                {expandedGroups[item.label] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {expandedGroups[item.label] && (
                <div className="mt-1 space-y-0.5">
                  {item.children.map((child) => (
                    <NavLink key={child.path} item={child} nested />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <NavLink key={item.path} item={item} />
          )
        )}
      </nav>

      <div className="px-4 pt-2 pb-1 text-center border-t border-[#DCE6DD]">
        <p className="text-[9px] text-[#6B8573] flex items-center justify-center gap-1">Made with <Heart size={9} className="text-red-500 fill-red-500" /> by Pavan Yerla for Bharat</p>
        <p className="text-[8px] text-[#6B8573] mt-0.5">&copy; 2026 SuperAgri.in</p>
        <div className="flex items-center justify-center gap-2 mt-0.5 text-[8px]">
          <Link to="/privacy" className="text-[#6B8573] hover:text-[#2D5438] underline">Privacy</Link>
          <Link to="/terms" className="text-[#6B8573] hover:text-[#2D5438] underline">Terms</Link>
          <Link to="/refund" className="text-[#6B8573] hover:text-[#2D5438] underline">Refund</Link>
        </div>
      </div>

      <div className="p-4 border-t border-[#DCE6DD]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#2D5438] flex items-center justify-center text-white text-sm font-medium">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1A2E20] truncate">{user?.name || "User"}</p>
            <Badge variant="secondary" className="text-[10px] bg-[#E8F0E5] text-[#2D5438]">
              {user?.role?.replace("_", " ")}
            </Badge>
          </div>
        </div>
        <Button
          data-testid="logout-btn"
          variant="ghost"
          size="sm"
          onClick={logout}
          className="w-full justify-start text-[#6B8573] hover:text-[#D9534F] hover:bg-red-50"
        >
          <LogOut size={16} className="mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F8F9F6]">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-[#DCE6DD] print-hidden">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-64 h-full bg-white shadow-xl">
            <button className="absolute top-4 right-4 text-[#6B8573]" onClick={() => setSidebarOpen(false)}>
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-[#DCE6DD] print-hidden">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-[#E8F0E5] transition-colors"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Sprout size={20} className="text-[#2D5438]" />
            <span className="font-semibold text-[#2D5438] font-['Manrope']">SuperAgri</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-pattern">
          <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
